import { opsMissionTypeEnum, property, targetPlatformEnum } from "../dataStruct";
import { getParameterOfArray, getParametersArray, getUtcTime, markDownProcessing, padLeft } from "../functions";
import { fppProvider } from "./providers/fppProvider";

export class notamReport {
  async createNotamMarkdown(flightId: string, classCom: any, firList: string[]): Promise<string> {
    const flightlistParams = await getParametersArray(flightId, "flightlist", opsMissionTypeEnum.all);

    // Here is the header part
    // =======================

    const mdText_lst: string[] = [];

    mdText_lst.push("## NOTAM REPORT");
    mdText_lst.push(
      "Flight **{FlightNumber}** -  Ops Mission Type: **{opsMissionType}** - revision {revision} @  {issueTime} \n"
    );
    mdText_lst.push(
      "ETD **{estTimeDep_gui_UTC} / {estTimeDep_gui_LT}**:   **{originName}** to **{destName}**,  Alternate {altName}\n"
    );
    mdText_lst.push("_____________");

    // replace values
    // The two reads need nothing from each other: they start together.
    const [flightRows, countRows] = await Promise.all([
      classCom.namedQuery("flightlistByFlightId", { flightId }),
      classCom.namedQuery("fuelCountByFlightId", { flightId }),
    ]);
    const flightNumber = flightRows[0].flightNumber;
    const revision: string = String(countRows[0]?.n ?? 0);
    const utcTimeNow = getUtcTime(new Date());
    const issueTime =
      padLeft(utcTimeNow.getHours().toString(), "0", 2) +
      ":" +
      padLeft(utcTimeNow.getMinutes().toString(), "0", 2) +
      " UTC";
    const estTimeDep: Date = new Date(
      Number(getParameterOfArray(flightId, "flightlist", property.estTimeDep, flightlistParams)[0]?.value ?? 0)
    );
    const estTimeDep_gui_LT =
      padLeft(estTimeDep.getHours().toString(), "0", 2) +
      ":" +
      padLeft(estTimeDep.getMinutes().toString(), "0", 2) +
      " LT";
    const estTimeDep_utc = getUtcTime(estTimeDep);
    const estTimeDep_gui_UTC =
      padLeft(estTimeDep_utc.getHours().toString(), "0", 2) +
      ":" +
      padLeft(estTimeDep_utc.getMinutes().toString(), "0", 2) +
      " UTC";

    const originIcao: string = getParameterOfArray(flightId, "flightlist", property.icaoOrigin, flightlistParams)[0]
      .value;
    const destIcao: string = getParameterOfArray(flightId, "flightlist", property.icaoDest, flightlistParams)[0].value;
    const altIcao: string = getParameterOfArray(flightId, "flightlist", property.icaoAlternate, flightlistParams)[0]
      .value;

    // Here is the content part
    // ========================

    const pushNotamSection = (label: string, result: any[]) => {
      mdText_lst.push("");
      mdText_lst.push(label);
      for (let i = 0; i < result.length; i++) {
        mdText_lst.push(
          this.formatNotamRow(
            result[i].id,
            result[i].location,
            result[i].startdate,
            result[i].enddate,
            result[i].message
          )
        );
      }
      if (result.length === 0) {
        mdText_lst.push("_no NOTAMS avail_");
      }
    };

    try {
      // The four reads need nothing from each other: they start together.
      const [originNotams, destNotams, altNotams, result] = await Promise.all(
        [[originIcao], [destIcao], [altIcao], firList].map((locations) =>
          classCom.namedQuery("notamsByLocations", { locations })
        )
      );

      pushNotamSection("## origin " + originIcao, originNotams);
      pushNotamSection("## destination " + destIcao, destNotams);
      pushNotamSection("## alternate " + altIcao, altNotams);

      // enroute
      const firListText = firList.length > 0 ? " " + firList.join(" ") : "";
      mdText_lst.push("");
      mdText_lst.push("## Enroute" + firListText);
      for (let i = 0; i < result.length; i++) {
        mdText_lst.push(
          this.formatNotamRow(
            result[i].id,
            result[i].location,
            result[i].startdate,
            result[i].enddate,
            result[i].message
          )
        );
      }

      if (result.length === 0) {
        mdText_lst.push("_no NOTAMS avail_");
      }

      let mdString: string = "";
      for (let i = 0; i < mdText_lst.length; i++) {
        mdString += mdText_lst[i] + "\n";
      }

      mdString = mdString.replace("{FlightNumber}", flightNumber);
      mdString = mdString.replace("{opsMissionType}", opsMissionTypeEnum[opsMissionTypeEnum.all].toUpperCase());
      mdString = mdString.replace("{revision}", revision);
      mdString = mdString.replace("{issueTime}", issueTime);
      mdString = mdString.replace("{estTimeDep_gui_UTC}", estTimeDep_gui_UTC);
      mdString = mdString.replace("{estTimeDep_gui_LT}", estTimeDep_gui_LT);
      mdString = mdString.replace("{originName}", originIcao);
      mdString = mdString.replace("{destName}", destIcao);
      mdString = mdString.replace("{altName}", altIcao);

      return mdString;
    } catch (err) {
      console.log("WARN: notams not yet available in DB"!);
      return "no NOTAMS yet available on this device!";
    }
  }

  formatNotamRow(id: string, location: string, startdate: number, enddate: number, message: string) {
    message = message.replace("/./g", ". <br>");

    const startDate_utc: string =
      getUtcTime(new Date(startdate)).toLocaleDateString() +
      " " +
      (getUtcTime(new Date(startdate)).toLocaleTimeString() + " UTC");
    const endDate_utc: string =
      getUtcTime(new Date(enddate)).toLocaleDateString() +
      " " +
      (getUtcTime(new Date(enddate)).toLocaleTimeString() + " UTC");

    let mdString: string =
      "| {id}       | A)  | {location}                      |\n" +
      "| --          | --   | --                           |\n" +
      "|            | B)  | {startDate_utc} C) {endDate_utc}|\n" +
      "|            | E)  | **{message}**                   |\n";

    mdString = mdString.replace("{id}", id);
    mdString = mdString.replace("{location}", location);
    mdString = mdString.replace("{startDate_utc}", startDate_utc);
    mdString = mdString.replace("{endDate_utc}", endDate_utc);
    mdString = mdString.replace("{message}", message);

    return mdString;
  }

  async getNotamHtmlReport(flightId: string, classCom: any, returnMarkdown: boolean = false): Promise<string> {
    const result = await classCom.namedQuery("notamProcessReportByFlight", { flightId });

    const mdp = new markDownProcessing();

    if (returnMarkdown) {
      return result[0].processReport;
    }

    const html: string = await mdp.convMarkdown(
      result[0].processReport,
      classCom.appearance,
      flightId,
      opsMissionTypeEnum.all,
      classCom,
      new fppProvider(),
      result[0].signatureId
    );

    if (classCom.targetPlatform == targetPlatformEnum.server) {
      const fs = classCom.package_fs;
      fs.writeFile("out.html", html, function (err) {
        if (!err) {
          const open = classCom.package_open;
          open("out.html");
        }
      });
    }

    return html;
  }

  async getProcessResultState(flightId: string, classCom: any): Promise<string> {
    const result = await classCom.namedQuery("notamProcessResultStateByFlight", { flightId });

    if (result.length == 0) {
      return "NIL";
    }

    if (result[0].processResultState == 0) {
      return "N/A";
    }

    if (result[0].processResultState == 1) {
      return "OK";
    }

    if (result[0].processResultState == 2) {
      return "WARN";
    }

    if (result[0].processResultState == 3) {
      return "FAIL";
    }

    return "N/A";
  }
}
